/**
 * 帳票の頭（#245、docs/94 B-4・★2 案B、Figma ④ 250:567）。見積書・請求書・領収書・{order}・見積依頼書・仕入・{paymentNotice}・見積書のテンプレート・比較の画面で使う。
 *   ・1段目・2段目＝主の色の帯（作成画面の帯と同じ --c-band-m。ダークも帯は暗い主の色）に白抜き（--c-on-primary）。3段目＝白（--c-surface）
 *     1段目：戻る（白の枠）｜自動保存（2行）・↶ ↷（白）・主ボタン（白地に主の色の字）・⋯（白の枠・右端）
 *     2段目：帳票の種類（メニューと同じ絵＋名前）・題（大きく）・状態チップ・？｜合計（税込）を大きく・税抜と消費税を下に
 *     3段目：名前が上・値が下の項目（宛先・日付・No.・{project} ›・元 › など）｜表示順・紙の形・社内用・詳細
 *   ・帯の上の状態チップは白 20% の地に白の字。朱のチップ（期限超過）は白地に朱の字と朱の輪（文字のまま）
 *   ・フォーカスの輪は帯の上では白（--c-focus を帯の中で --c-on-primary に）
 *   ・高さは抑えない。1280px で3段目が入らないときは3段目を折り返す（はみ出させない・金額を「…」で切らない）
 */
import type { ReactNode } from "react";
import { AreaIcon, type Area } from "./AreaIcon.jsx";
import { BackButton, type BackNav } from "./BackButton.jsx";

export function DocHead({
  back,
  save,
  undo,
  primary,
  menu,
  kind,
  kindLabel,
  title,
  chips,
  help,
  total,
  totalNode,
  meta,
  tools,
  testId,
  children,
}: {
  back: BackNav;
  save?: ReactNode;
  undo?: ReactNode;
  primary?: ReactNode;
  menu?: ReactNode;
  kind: Area;
  kindLabel: string;
  title: ReactNode;
  chips?: ReactNode;
  help?: ReactNode;
  /** 合計（税込）と税抜・消費税。無い帳票（見積依頼書）は省く */
  total?: { total: number; subtotal: number; taxTotal: number; label?: string } | null;
  /** 合計の場所に自分で描くもの（比較の画面の「前 → 後」） */
  totalNode?: ReactNode;
  meta?: ReactNode;
  tools?: ReactNode;
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <div className="dochead" data-testid={testId}>
      <div className="dh-band">
        <div className="dh-r1">
          <BackButton back={back} light />
          <span className="sp" />
          {save ? <span className="dh-save">{save}</span> : null}
          {undo}
          {primary}
          {menu ? <span className="hd-acts dh-menu" data-testid="head-dots">{menu}</span> : null}
        </div>
        <div className="dh-r2">
          <div className="dh-tl">
            {/* 画面の題（h1）は帳票の種類（見積書・請求書・{order}…）。件名・工事名は下の大きい題 */}
            <h1 className="dh-kind">
              <AreaIcon a={kind} />
              {kindLabel}
            </h1>
            <div className="dh-title">
              {title}
              {chips}
              {help}
            </div>
          </div>
          {totalNode ? <div className="dh-tot">{totalNode}</div> : total ? (
            <div className="dh-tot">
              <div className="n">
                <span className="tl">{total.label ?? "合計（税込）"}</span>
                <span className="num" id="hdTotal">{total.total.toLocaleString("ja-JP")}</span>
                <span className="yen">円</span>
              </div>
              <div className="sub num">
                税抜 {total.subtotal.toLocaleString("ja-JP")}円　消費税 {total.taxTotal.toLocaleString("ja-JP")}円
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {meta || tools ? (
        <div className="dh-r3">
          <div className="dh-meta">{meta}</div>
          {tools ? <div className="dh-tools">{tools}</div> : null}
        </div>
      ) : null}
      {children ? <div className="dh-more">{children}</div> : null}
    </div>
  );
}

/** 3段目の項目（名前が上・値が下） */
export function DocField({ label, children, testId }: { label: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <div className="dh-f" data-testid={testId}>
      <span className="dh-fl">{label}</span>
      <span className="dh-fv">{children}</span>
    </div>
  );
}

/** 題の欄（件名）の幅：中身の字数に合わせる（全角 1.04em・半角 0.6em。見積書の工事名の欄と同じ測り方） */
export const subjWidth = (v: string) => ({ width: `calc(${Math.max(6, [...v].reduce((a, c) => a + (c.charCodeAt(0) < 0x2000 ? 0.6 : 1.04), 0) + 1)}em + 18px)` });
